/* ============================================================
   PRUUVO PROGRESSIVE ENHANCEMENTS
   Zero-dependency vanilla JS. Loaded with defer from theme.liquid.
   Each enhancement no-ops cleanly when its markup is absent.
   ============================================================ */

(function () {
  'use strict';

  /* ── Announcement bar dismiss ──
     Buttons carry data-pruuvo-dismiss="<key>"; hides the closest
     section wrapper and remembers the choice for the session. */
  function initAnnouncementDismiss() {
    document.querySelectorAll('[data-pruuvo-dismiss]').forEach(function (btn) {
      var key = 'pruuvo:dismissed:' + btn.dataset.pruuvoDismiss;
      var target = btn.closest('.shopify-section') || btn.closest('.announcement--bar');
      if (!target) return;
      try {
        if (sessionStorage.getItem(key)) {
          target.style.display = 'none';
          return;
        }
      } catch (e) { /* storage unavailable — dismiss still works, just not persisted */ }
      btn.addEventListener('click', function () {
        target.style.display = 'none';
        try { sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
      });
    });
  }

  /* ── Check-warranty order filter ──
     Logged-in state only: the lookup input filters the already-rendered
     order cards by order number. No API calls — see the check-warranty
     section for the login-gate behavior when logged out. */
  function initWarrantyFilter() {
    var form = document.querySelector('[data-pruuvo-warranty-filter]');
    if (!form) return;
    var input = form.querySelector('input[name="order_id"]');
    var cards = document.querySelectorAll('.pruuvo-warranty-order[data-order-number]');
    var notFound = document.querySelector('[data-pruuvo-warranty-notfound]');
    if (!input || !cards.length) return;

    function applyFilter() {
      var query = input.value.trim().toLowerCase().replace(/^#/, '');
      var visible = 0;
      cards.forEach(function (card) {
        var match = !query || card.dataset.orderNumber.indexOf(query) !== -1;
        card.classList.toggle('hidden', !match);
        if (match) visible++;
      });
      if (notFound) notFound.classList.toggle('hidden', visible > 0 || !query);
    }

    form.addEventListener('submit', applyFilter);
    input.addEventListener('input', applyFilter);
  }

  /* ── Drag-to-scroll for horizontal rows ──
     Applies to .pruuvo-scroll-row containers (e.g. the category grid
     slider). Suppresses the click that would otherwise fire on the
     card after a drag. */
  function initDragScroll() {
    document.querySelectorAll('.pruuvo-scroll-row').forEach(function (el) {
      var isDown = false;
      var dragged = false;
      var startX = 0;
      var scrollLeft = 0;

      el.addEventListener('mousedown', function (e) {
        isDown = true;
        dragged = false;
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
      });
      ['mouseleave', 'mouseup'].forEach(function (evt) {
        el.addEventListener(evt, function () {
          isDown = false;
        });
      });
      el.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var walk = e.pageX - el.offsetLeft - startX;
        if (Math.abs(walk) > 4) dragged = true;
        el.scrollLeft = scrollLeft - walk;
      });
      el.addEventListener('click', function (e) {
        if (dragged) {
          e.preventDefault();
          e.stopPropagation();
          dragged = false;
        }
      }, true);
    });
  }

  /* ── Sticky add-to-cart bar (PDP) ──
     Reveals the sticky bar once the main ATC button leaves the viewport,
     hides it again when the main ATC scrolls back into view. */
  function initStickyAtc() {
    var bar = document.querySelector('[data-pruuvo-sticky-atc]');
    if (!bar) return;
    var mainAtc = document.querySelector('.product-form__submit');
    if (!mainAtc || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var show = !entry.isIntersecting;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { threshold: 0 });

    observer.observe(mainAtc);
  }

  /* ── Animated discount counter ──
     When a discount badge first enters view, counts up from 0 to its
     value. Preserves the theme's locale suffix (e.g. "35% off") via
     data-discount-suffix rather than assuming a "-N%" format. */
  function animateCounter(el) {
    var target = parseInt(el.dataset.discount, 10);
    if (isNaN(target)) return;
    var suffix = el.dataset.discountSuffix || '%';
    var duration = 600;
    var start = performance.now();

    function update(now) {
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * target) + suffix;
      if (progress < 1) requestAnimationFrame(update);
    }
    requestAnimationFrame(update);
  }

  function initDiscountCounters() {
    var badges = document.querySelectorAll('[data-discount]');
    if (!badges.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    badges.forEach(function (el) { observer.observe(el); });
  }

  /* ── Page transitions (View Transitions API) ──
     Progressive: only same-origin, same-tab, non-modified navigations.
     Browsers without the API keep normal navigation. */
  function initViewTransitions() {
    if (!document.startViewTransition) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest('a[href]');
      if (!link) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      if (link.getAttribute('href').charAt(0) === '#') return;
      var url;
      try { url = new URL(link.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;

      e.preventDefault();
      document.startViewTransition(function () {
        window.location = link.href;
      });
    });
  }

  function init() {
    initAnnouncementDismiss();
    initWarrantyFilter();
    initDragScroll();
    initStickyAtc();
    initDiscountCounters();
    initViewTransitions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
